import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Container from 'components/UI/Layout/Container';
import Row from 'components/UI/Layout/Row';
import Col from 'components/UI/Layout/Col';
import EditableTextInput from 'components/UI/Input/EditableTextInput';
import FormComponent from 'components/UI/Input/FormComponent';
import Form from 'services/validation/Form';
import entityFactory from 'services/entity-factory';
import travelAgentDefinition from 'services/entity-factory/travelAgent';
import { common as travelAgentBasicRules, travelAgentDataRules } from 'services/validation/rules/travelAgent.rules';
import { isEmpty, isNil } from 'lodash';
import produce from 'immer';
import MembersRepository from 'services/repositories/MembersRepository';
import ExternalRewardsRepository from 'services/repositories/ExternalRewardsRepository';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import ErrorMessage from 'components/UI/ErrorMessage';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';

function SignUpMemberForm({ nextStep, formData, externalRewardType, isB2C })
{
    const [form, setForm] = useState(new Form(entityFactory(travelAgentDefinition), travelAgentBasicRules));
    const [fetchedTravelAgent, setFetchedTravelAgent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleFormChange = (_form) =>
    {
        setForm(_form);
    };

    const fetchTravelAgent = async () =>
    {
        setIsLoading(true);

        const response = await (new MembersRepository()).getByEmail(form.data.email);

        setFetchedTravelAgent(true);

        if (!response.isSuccess())
        {
            setIsLoading(false);
            return false;
        }

        const responseData = response.getData();

        if (isEmpty(responseData) || isEmpty(responseData.member))
        {
            const formValidations = {
                ...travelAgentBasicRules,
                ...travelAgentDataRules,
            };
            setForm(new Form(form.data, formValidations));
            setIsLoading(false);
            return true;
        }

        const _form = produce(form, (draft) =>
        {
            draft.data.id = responseData.member.id;
            draft.data.username = responseData.member.username;
            draft.data.first_name = responseData.member.first_name;
            draft.data.last_name = responseData.member.last_name;
            draft.data.address = responseData.member.address;
            draft.data.city = responseData.member.city;
            draft.data.state = responseData.member.state;
            draft.data.country = responseData.member.country;
            draft.data.zip = responseData.member.zip;
            draft.data.travel_agency_id = responseData.member.travel_agency_id;
        });

        setForm(_form);

        // Set Pending Enrollment
        if (!isNil(externalRewardType))
        {
            const pendingEnrollmentData = {
                id: form.data.id,
                external_reward_type: externalRewardType,
            };

            await (new ExternalRewardsRepository()).setPendingEnrollment(pendingEnrollmentData);
        }

        // Continue to the next step
        setIsLoading(false);
        const travelAgentData = {
            ..._form.data,
            travelAgency: responseData.member.travelAgency,
        };
        nextStep({ ...formData, travelAgent: travelAgentData });
        return true;
    };

    const continueStep = async () =>
    {
        setErrorMessage('');
        const _form = produce(form, (draft) =>
        {
            draft.validate();
        });

        setForm({ ..._form });

        if (!fetchedTravelAgent && _form.isValid())
        {
            fetchTravelAgent().finally(() =>
            {
                setIsLoading(false);
            });
        }

        if (fetchedTravelAgent && _form.isValid())
        {
            setIsLoading(true);
            const submit = await nextStep({ ...formData, travelAgent: _form.data });
            if (!submit)
            {
                setIsLoading(false);
                setErrorMessage('There was an error while saving. Please try again.');
            }
        }
    };

    return (
        <FormComponent source={form} onFormChange={handleFormChange}>
            <Container className="tw-px-4 tw-mb-4 tw-rounded-md">
                {!isB2C && (
                    <Row className="tw-mb-3 tw-mt-3 tw-justify-center">
                        <span className="tw-font-medium">Please provide travel agent information</span>
                    </Row>
                )}
                <ErrorMessage errorMessage={errorMessage} />
                <Row className="tw-mb-3">
                    <Col md={12} sm={12} xs={12} spacing={2}>
                        <EditableTextInput
                            editing={!fetchedTravelAgent && !isLoading}
                            labelText="Email"
                            text={form.data.email}
                            inputProps={{
                                path: 'email',
                                error: form.errorsBag.errors.email,
                            }}
                        />
                    </Col>
                </Row>

                { (fetchedTravelAgent && !form.data.id)
                    && (
                        <>
                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="First Name"
                                        text={form.data.first_name}
                                        inputProps={{
                                            path: 'first_name',
                                            error: form.errorsBag.errors.first_name,
                                        }}
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Last Name"
                                        inputProps={{
                                            path: 'last_name',
                                            error: form.errorsBag.errors.last_name,
                                        }}
                                        text={form.data.last_name}
                                    />
                                </Col>
                            </Row>
                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Address"
                                        inputProps={{
                                            path: 'address',
                                            error: form.errorsBag.errors.address,
                                        }}
                                        text={form.data.address}
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="City"
                                        inputProps={{
                                            path: 'city',
                                            error: form.errorsBag.errors.city,
                                        }}
                                        text={form.data.city}
                                    />
                                </Col>
                            </Row>

                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="State"
                                        inputProps={{
                                            path: 'state',
                                            error: form.errorsBag.errors.state,
                                        }}
                                        text={form.data.state}
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Country"
                                        inputProps={{
                                            path: 'country',
                                            error: form.errorsBag.errors.country,
                                        }}
                                        text={form.data.country}
                                    />
                                </Col>
                            </Row>

                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Zip"
                                        inputProps={{
                                            path: 'zip',
                                            error: form.errorsBag.errors.zip,
                                        }}
                                        text={form.data.zip}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                <Row className="tw-justify-center">
                    <Button
                        className="tw-pb-1.5 tw-pt-2"
                        onClick={() => continueStep()}
                        disabled={isLoading}
                    >
                        Next {!isLoading && <FontAwesomeIcon className="tw-ml-4" icon={faChevronRight} />} <LoadingSpinner className="tw-ml-4" loading={isLoading} />
                    </Button>
                </Row>
            </Container>
        </FormComponent>
    );
}

SignUpMemberForm.defaultProps = {
    isB2C: false,
};

SignUpMemberForm.propTypes = {
    nextStep: PropTypes.func,
    formData: PropTypes.any,
    externalRewardType: PropTypes.any,
    isB2C: PropTypes.bool,
};

export default SignUpMemberForm;
